import React from 'react';
import type {ButtonProps, IconButtonProps} from '@tempo/core';
import {Button, IconButton, useBreakpointValue} from '@tempo/core';
import Tooltip from '@components/Tooltip';

import type {Breakpoints} from '@deezer/next/services/types/breakpoints';
type Breakpoint = keyof Breakpoints;

type AutoIconButtonProps = {
	'aria-label': string;
	'data-testid'?: string;
	tooltip?: string;
	iconButtonBreakpoint?: Breakpoint;
	iconButtonProps?: IconButtonProps;
} & IconButtonProps &
	ButtonProps;

const AutoIconButton = ({
	['aria-label']: ariaLabel,
	['data-testid']: dataTestId,
	leftIcon,
	rightIcon,
	icon,
	tooltip,
	isRound,
	loadingText,
	iconSpacing,
	spinnerPlacement,
	iconButtonBreakpoint = 'md',
	iconButtonProps,
	display,
	...buttonProps
}: AutoIconButtonProps) => {
	const displayValue = useBreakpointValue(display);

	const maybeWrapTooltip = (node: React.ReactElement) => {
		if (!tooltip) {
			return node;
		}

		return (
			<Tooltip label={tooltip} hasArrow>
				{node}
			</Tooltip>
		);
	};

	const iconBtn = (
		<IconButton
			aria-label={ariaLabel}
			data-testid={dataTestId ? `${dataTestId}-icon-button` : undefined}
			icon={icon || leftIcon || rightIcon}
			isRound={isRound}
			{...buttonProps}
			{...iconButtonProps}
			display={
				displayValue === 'none'
					? 'none'
					: {
							base: 'flex',
							[iconButtonBreakpoint]: 'none',
						}
			}
		/>
	);

	const btn = (
		<Button
			data-testid={dataTestId}
			leftIcon={leftIcon}
			rightIcon={rightIcon}
			loadingText={loadingText}
			iconSpacing={iconSpacing}
			spinnerPlacement={spinnerPlacement}
			{...buttonProps}
			display={
				displayValue === 'none'
					? 'none'
					: {
							base: 'none',
							[iconButtonBreakpoint]: 'flex',
						}
			}
		/>
	);

	return (
		<>
			{maybeWrapTooltip(iconBtn)}
			{maybeWrapTooltip(btn)}
		</>
	);
};

export default AutoIconButton;
